import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  MessageCircle, Ticket, RefreshCw, Copy, ExternalLink,
  MessageSquare, Bell, BellRing, CheckCircle2, Image as ImageIcon, Paperclip,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import PhoneAction from "@/components/PhoneAction";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WhatsApp tab — Apeluri Recenzii CRM (INDEPENDENT de pagina /whatsapp globală).
 * Fiecare conversație afișează:
 *   1. Link către comanda din tab-ul „Comenzi" (query param `?order=`).
 *   2. Status apel (pill amber).
 *   3. Status recenzie (pill emerald/zinc).
 *   4. Badge-uri „Trimis": Mesaj Recenzie / R1 / R2 (doar dacă *_sent_at există).
 *   5. Buton „Trimite voucher" — rezervă atomic FIFO următorul Disponibil + mark ca „oferit".
 *   6. Pe mesajele inbound cu imagine: buton „Adaugă ca dovadă" → creează proof + review = Confirmată.
 */
export default function Whatsapp() {
  // ─── Persistență localStorage ─────────────────────────────────────
  // Ținem snapshot-ul conversațiilor + thread-uri deschise recent în
  // localStorage. La mount le afișăm INSTANT (fără spinner) — apoi
  // fetch-ul de background le împrospătează doar dacă chiar s-au schimbat.
  const CONVS_KEY = "bragus_wa_convs_v1";
  const THREAD_KEY_PREFIX = "bragus_wa_thread_v1::";
  const readCache = (k) => {
    try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; }
    catch { return null; }
  };
  const writeCache = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

  const [convs, setConvs] = useState(() => readCache(CONVS_KEY) || []);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [attaching, setAttaching] = useState(null); // message_id currently uploading
  // Long-press → arată buton delete roșu peste rândul conversației.
  const [deleteMode, setDeleteMode] = useState(null); // phone or null
  const [deletingPhone, setDeletingPhone] = useState(null);
  const pressTimerRef = useRef(null);
  const pressedRef = useRef(false); // true dacă long-press s-a declanșat (blocăm click-ul)

  // Hash rapid pt detectat schimbări — folosit pt a evita re-render inutil.
  const convsFingerprint = (list) =>
    (list || [])
      .map((c) => `${c.phone}|${c.last_message_at || ""}|${c.unread_count || 0}|${c.last_message_text || ""}`)
      .join("~");
  const threadFingerprint = (msgs) =>
    (msgs || []).map((m) => `${m.message_id || m.wa_id || ""}|${m.timestamp || m.created_at || ""}|${m.is_read ? 1 : 0}`).join("~");

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/whatsapp/conversations`);
      const next = r.data?.conversations || [];
      // Actualizează state DOAR dacă amprenta e diferită — altfel niciun re-render.
      setConvs((prev) => (convsFingerprint(prev) === convsFingerprint(next) ? prev : next));
      writeCache(CONVS_KEY, next);
      // Re-sync selected obiect ca să reflecte counters/timestamp actualizate.
      if (selected) {
        const nextSel = next.find((c) => c.phone === selected.phone);
        if (nextSel && JSON.stringify(nextSel) !== JSON.stringify(selected)) setSelected(nextSel);
      }
    } catch (e) {
      if (!silent) toast.error("Nu am putut încărca conversațiile", { description: e.response?.data?.detail || e.message });
    } finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Auto-poll la fiecare 8s: reîncarcă lista + thread-ul deschis, ca să apară
  // mesajele noi fără refresh manual la pagină. Se oprește când tab-ul e în background.
  // Silent=true → nu declanșează spinner; setState-ul se face doar când amprenta diferă.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      load({ silent: true });
      if (selected?.phone) {
        axios.get(`${API}/apeluri-recenzii/whatsapp/conversation/${encodeURIComponent(selected.phone)}`)
          .then((r) => {
            const nextMsgs = r.data?.messages || [];
            setThread((prev) => (threadFingerprint(prev) === threadFingerprint(nextMsgs) ? prev : nextMsgs));
            writeCache(THREAD_KEY_PREFIX + selected.phone, nextMsgs);
          })
          .catch(() => {});
      }
    };
    const id = setInterval(tick, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.phone]);

  const openThread = async (c) => {
    setSelected(c);
    // Afișează instant din cache dacă avem — evită "loading blank" până răspunde API-ul.
    const cached = readCache(THREAD_KEY_PREFIX + c.phone);
    if (cached && cached.length) setThread(cached);
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/whatsapp/conversation/${encodeURIComponent(c.phone)}`);
      const msgs = r.data?.messages || [];
      setThread((prev) => (threadFingerprint(prev) === threadFingerprint(msgs) ? prev : msgs));
      writeCache(THREAD_KEY_PREFIX + c.phone, msgs);
    } catch { if (!cached) setThread([]); }
  };

  const sendVoucher = async (c) => {
    if (!window.confirm(`Trimit un voucher către ${c.name} (${c.phone})?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/apeluri-recenzii/vouchers/send-next`, {
        order_id: String(c.order_id),
        customer_name: c.name,
        phone: c.phone,
      });
      toast.success(`Voucher ${r.data.voucher.code} trimis`, {
        description: `${r.data.voucher.value_ron.toFixed(2)} RON — status: Oferit`,
      });
      await load();
      if (selected?.phone === c.phone) await openThread(c);
    } catch (e) {
      toast.error("Trimitere eșuată", { description: e.response?.data?.detail || e.message });
    } finally { setSending(false); }
  };

  const attachAsProof = async (msg) => {
    const url = getImageUrl(msg);
    if (!url && !msg?.media_id) { toast.error("Mesajul nu are imagine detectată"); return; }
    if (!window.confirm("Salvez această imagine ca dovadă recenzie?\n\nStatus recenzie devine „Recenzie Confirmată”."))
      return;
    setAttaching(msg.id || msg.wa_id);
    try {
      await axios.post(
        `${API}/apeluri-recenzii/whatsapp/conversation/${encodeURIComponent(selected.phone)}/attach-proof`,
        {
          image_url: url,
          media_id: msg.media_id || null,
          message_id: msg.id || msg.wa_id,
          content_type: msg.content_type || msg.mime_type,
          filename: msg.filename,
        },
      );
      toast.success("Dovadă salvată — recenzie confirmată");
      await load();
    } catch (e) {
      toast.error("Nu am putut salva dovada", { description: e.response?.data?.detail || e.message });
    } finally { setAttaching(null); }
  };

  // Long-press pe rândul conversației (~500 ms) → arată butonul delete.
  const startPress = (c) => {
    pressedRef.current = false;
    clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      pressedRef.current = true;
      setDeleteMode(c.phone);
    }, 500);
  };
  const cancelPress = () => {
    clearTimeout(pressTimerRef.current);
  };
  const deleteConversation = async (c, e) => {
    e?.stopPropagation?.();
    if (!window.confirm(`Ștergi TOATE mesajele conversației cu ${c.name} (${c.phone})?\n\nAcțiunea nu poate fi anulată.`)) {
      setDeleteMode(null);
      return;
    }
    setDeletingPhone(c.phone);
    try {
      const r = await axios.delete(`${API}/apeluri-recenzii/whatsapp/conversation/${encodeURIComponent(c.phone)}`);
      toast.success(`Conversație ștearsă`, { description: `${r.data?.deleted || 0} mesaje eliminate.` });
      setConvs((prev) => prev.filter((x) => x.phone !== c.phone));
      if (selected?.phone === c.phone) { setSelected(null); setThread([]); }
    } catch (err) {
      toast.error("Nu am putut șterge", { description: err.response?.data?.detail || err.message });
    } finally {
      setDeletingPhone(null);
      setDeleteMode(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="whatsapp-tab">
      <div className="flex items-center justify-end">
        <button
          onClick={load}
          disabled={loading}
          data-testid="wa-reload"
          className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1 text-xs text-zinc-200 hover:border-zinc-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Reîncarcă
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[380px_1fr]">
        {/* Left column — conversations list */}
        <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950">
          <div className="border-b border-zinc-800 px-3 py-2 text-[11px] uppercase text-zinc-500">
            Conversații ({convs.length})
          </div>
          <div className="max-h-[600px] overflow-y-auto divide-y divide-zinc-900">
            {convs.length === 0 && !loading && (
              <div className="p-4 text-center text-sm text-zinc-500">
                Nu există conversații încă. Trimite un voucher primul.
              </div>
            )}
            {convs.map((c) => {
              const showDelete = deleteMode === c.phone;
              const isDeleting = deletingPhone === c.phone;
              return (
                <div
                  key={c.phone}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (pressedRef.current) { pressedRef.current = false; return; }
                    if (showDelete) { setDeleteMode(null); return; }
                    openThread(c);
                  }}
                  onMouseDown={() => startPress(c)}
                  onMouseUp={cancelPress}
                  onMouseLeave={cancelPress}
                  onTouchStart={() => startPress(c)}
                  onTouchEnd={cancelPress}
                  onTouchCancel={cancelPress}
                  onContextMenu={(e) => { e.preventDefault(); setDeleteMode(c.phone); }}
                  data-testid={`wa-conv-${c.phone}`}
                  className={`relative w-full cursor-pointer select-none px-3 py-2 text-left transition hover:bg-zinc-900/50 ${
                    selected?.phone === c.phone ? "bg-zinc-900/70" : ""
                  } ${showDelete ? "ring-1 ring-rose-500/40" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-zinc-100">{c.name}</span>
                    <span className="text-[10px] text-zinc-500">
                      {c.last_message_at ? new Date(c.last_message_at).toLocaleDateString("ro-RO") : ""}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px]">
                    <span className="font-mono text-sky-300">{c.phone}</span>
                    {c.review_status === "recenzie_confirmata" && (
                      <span
                        className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-emerald-300"
                        title="Recenzie confirmată"
                      >
                        <CheckCircle2 className="h-2.5 w-2.5" /> conf.
                      </span>
                    )}
                    {c.last_voucher_code && (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-amber-300">
                        <Ticket className="h-2.5 w-2.5" /> {c.last_voucher_code}
                      </span>
                    )}
                  </div>
                  {/* Feb 2026 — status trimitere Mesaj Recenzie / R1 / R2.
                      Emerald cu bifă = trimis (tooltip data), zinc gol = netrimis. */}
                  <div className="mt-1 flex items-center gap-1 text-[10px]">
                    <SendStatusBadge
                      label="MR"
                      sent={Boolean(c.message_sent)}
                      sentAt={c.message_sent_at}
                      title="Mesaj Recenzie"
                    />
                    <SendStatusBadge
                      label="R1"
                      sent={Boolean(c.reminder_1)}
                      sentAt={c.reminder_1_sent_at}
                      title="Reminder 1"
                    />
                    <SendStatusBadge
                      label="R2"
                      sent={Boolean(c.reminder_2)}
                      sentAt={c.reminder_2_sent_at}
                      title="Reminder 2"
                    />
                  </div>
                  {showDelete && (
                    <button
                      type="button"
                      onClick={(e) => deleteConversation(c, e)}
                      disabled={isDeleting}
                      data-testid={`wa-conv-delete-${c.phone}`}
                      title="Șterge conversația"
                      className="absolute right-2 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 rounded-md border border-rose-500/50 bg-rose-500/90 px-2 py-1 text-xs font-medium text-white shadow-lg hover:bg-rose-500 disabled:opacity-60"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {isDeleting ? "Șterg…" : "Șterge"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right column — active thread */}
        <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950">
          {!selected ? (
            <div className="flex h-full min-h-[400px] items-center justify-center text-sm text-zinc-500">
              Selectează o conversație din stânga.
            </div>
          ) : (
            <div className="flex h-full flex-col">
              <div className="border-b border-zinc-800 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-zinc-100" data-testid={`wa-header-name-${selected.phone}`}>
                      {selected.name}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                      <PhoneAction phone={selected.phone} />
                      {/* 1) Nr comandă cu link → tab Comenzi din Apeluri Recenzii */}
                      <Link
                        to={`/apeluri-recenzii?order=${encodeURIComponent(selected.order_id)}&phone=${encodeURIComponent(selected.phone)}`}
                        className="inline-flex items-center gap-1 rounded-md border border-orange-500/30 bg-orange-500/10 px-2 py-1 text-orange-100 hover:bg-orange-500/20"
                        title="Deschide comanda în tab-ul Comenzi"
                        data-testid={`wa-order-link-${selected.phone}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        #{selected.order_id}
                      </Link>
                      {/* 2) Status apel */}
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 font-medium text-amber-200"
                        title="Status apel"
                        data-testid={`wa-call-status-${selected.phone}`}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                        {selected.call_status_label}
                      </span>
                      {/* 3) Status recenzie */}
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${
                          selected.review_status === "recenzie_confirmata"
                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                            : "border-zinc-700 bg-zinc-800/60 text-zinc-300"
                        }`}
                        title="Status recenzie"
                        data-testid={`wa-review-status-${selected.phone}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${
                          selected.review_status === "recenzie_confirmata" ? "bg-emerald-400" : "bg-zinc-400"
                        }`} />
                        {selected.review_status === "recenzie_confirmata" ? "Recenzie confirmată" : "Fără recenzie"}
                      </span>
                    </div>
                    {/* 4) Badge-uri trimiteri automate — doar cele deja trimise */}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
                      {selected.message_sent_at && (
                        <SentBadge
                          testId={`wa-sent-mesaj-${selected.phone}`}
                          icon={<MessageSquare className="h-2.5 w-2.5" />}
                          label="Mesaj Recenzie"
                          at={selected.message_sent_at}
                          color="sky"
                        />
                      )}
                      {selected.reminder_1_sent_at && (
                        <SentBadge
                          testId={`wa-sent-r1-${selected.phone}`}
                          icon={<Bell className="h-2.5 w-2.5" />}
                          label="R1"
                          at={selected.reminder_1_sent_at}
                          color="violet"
                        />
                      )}
                      {selected.reminder_2_sent_at && (
                        <SentBadge
                          testId={`wa-sent-r2-${selected.phone}`}
                          icon={<BellRing className="h-2.5 w-2.5" />}
                          label="R2"
                          at={selected.reminder_2_sent_at}
                          color="fuchsia"
                        />
                      )}
                      {!selected.message_sent_at && !selected.reminder_1_sent_at && !selected.reminder_2_sent_at && (
                        <span className="text-zinc-600">Nimic trimis automat încă</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => sendVoucher(selected)}
                    disabled={sending}
                    data-testid={`wa-send-voucher-${selected.phone}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-400 disabled:opacity-60"
                  >
                    <Ticket className="h-3.5 w-3.5" />
                    {sending ? "Se trimite…" : "Trimite voucher"}
                  </button>
                </div>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto p-3">
                {thread.map((m, i) => {
                  const isImage = detectImage(m);
                  const imgUrl = getImageUrl(m);
                  const attachBusy = attaching === (m.id || m.wa_id);
                  const textVal = m.text || m.content || "";
                  return (
                    <div
                      key={m.id || m.wa_id || i}
                      className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}
                      data-testid={`wa-msg-${i}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                          m.direction === "outbound"
                            ? "bg-emerald-500/15 text-emerald-100"
                            : "bg-zinc-800 text-zinc-100"
                        }`}
                      >
                        {isImage && imgUrl ? (
                          <div className="space-y-1.5">
                            <img
                              src={imgUrl}
                              alt="wa-attachment"
                              className="max-h-64 rounded border border-zinc-700 object-contain"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                            {textVal && <div>{textVal}</div>}
                          </div>
                        ) : (
                          <div className="whitespace-pre-wrap">{textVal || "—"}</div>
                        )}
                        <div className="mt-1 flex items-center justify-between gap-2 text-[10px] opacity-80">
                          <span>{m.timestamp ? new Date(m.timestamp).toLocaleString("ro-RO") : ""}</span>
                          <div className="flex items-center gap-1">
                            {/* 6) Buton „Adaugă ca dovadă" — doar pe inbound cu imagine */}
                            {m.direction === "inbound" && isImage && imgUrl && (
                              <button
                                type="button"
                                onClick={() => attachAsProof(m)}
                                disabled={attachBusy}
                                data-testid={`wa-attach-proof-${i}`}
                                className="inline-flex items-center gap-1 rounded bg-sky-500/20 px-1.5 py-0.5 text-sky-100 hover:bg-sky-500/30 disabled:opacity-50"
                                title="Salvează imaginea ca dovadă recenzie (status devine Confirmată)"
                              >
                                <Paperclip className="h-2.5 w-2.5" />
                                {attachBusy ? "Se salvează…" : "Adaugă ca dovadă"}
                              </button>
                            )}
                            {m.voucher_code && (
                              <button
                                type="button"
                                onClick={() => { navigator.clipboard.writeText(textVal); toast.success("Copiat"); }}
                                className="inline-flex items-center gap-1 rounded px-1 hover:bg-emerald-500/20"
                                title="Copiază mesajul"
                              >
                                <Copy className="h-2.5 w-2.5" /> Copiază
                              </button>
                            )}
                            {m.delivery_status === "pending_meta_api" && (
                              <span className="rounded bg-amber-500/20 px-1 text-amber-200">
                                Local — Meta API pending
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {thread.length === 0 && (
                  <div className="py-8 text-center text-xs text-zinc-500">
                    <MessageCircle className="mx-auto mb-1 h-4 w-4 opacity-40" />
                    Nici un mesaj încă. Apasă „Trimite voucher" ca să începi.
                  </div>
                )}
              </div>
              {/* Text input pentru reply liber (2026-02).
                  Meta permite text liber în 24h de la ultimul inbound. */}
              <FreeformComposer phone={selected.phone} onSent={() => { openThread(selected); load(); }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FreeformComposer({ phone, onSent }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const submit = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/apeluri-recenzii/whatsapp/conversation/${encodeURIComponent(phone)}/send-message`, { text: t });
      setText("");
      onSent?.();
      toast.success("Mesaj trimis");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error("Nu s-a putut trimite", { description: String(detail) });
    } finally { setSending(false); }
  };
  const onPickFile = () => fileRef.current?.click();
  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Fișier prea mare (max 15 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (text.trim()) fd.append("caption", text.trim());
      await axios.post(
        `${API}/apeluri-recenzii/whatsapp/conversation/${encodeURIComponent(phone)}/send-media`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setText("");
      onSent?.();
      toast.success(`Trimis: ${f.name}`);
    } catch (err) {
      toast.error("Atașament eșuat", { description: err.response?.data?.detail || err.message });
    } finally { setUploading(false); }
  };
  return (
    <form
      onSubmit={submit}
      className="flex items-center gap-2 border-t border-zinc-800 bg-zinc-950/60 p-2"
      data-testid="wa-composer-form"
    >
      <input
        ref={fileRef}
        type="file"
        onChange={onFileChosen}
        accept="image/*,application/pdf,video/mp4,audio/mpeg,audio/ogg,audio/aac"
        className="hidden"
        data-testid="wa-composer-file-input"
      />
      <button
        type="button"
        onClick={onPickFile}
        disabled={uploading || sending}
        data-testid="wa-composer-attach"
        title="Atașează fișier (poză, PDF, audio, video — max 15 MB)"
        className="inline-flex items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-2 text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-200 disabled:opacity-50"
      >
        {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
      </button>
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={uploading ? "Se încarcă atașamentul…" : "Scrie un mesaj…"}
        disabled={sending || uploading}
        maxLength={4096}
        data-testid="wa-composer-input"
        className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-emerald-500/60 focus:outline-none disabled:opacity-50"
      />
      <button
        type="submit"
        disabled={sending || uploading || !text.trim()}
        data-testid="wa-composer-send"
        className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50"
      >
        {sending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
        Trimite
      </button>
    </form>
  );
}

// Helpers ---------------------------------------------------------------------

function detectImage(m) {
  const t = String(m?.type || "").toLowerCase();
  if (t === "image" || t === "photo") return true;
  const ct = String(m?.content_type || m?.mime_type || "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  const url = getImageUrl(m);
  if (url && /\.(jpe?g|png|webp|gif|heic|heif)(\?|$)/i.test(url)) return true;
  return Boolean(m?.media_id);
}

function getImageUrl(m) {
  if (m?.media_id) return `${API}/whatsapp/media/${m.media_id}`;
  return (
    m?.image_url ||
    m?.media_url ||
    m?.attachment_url ||
    (typeof m?.content === "string" && /^https?:\/\//i.test(m.content) ? m.content : null) ||
    null
  );
}

function SentBadge({ testId, icon, label, at, color }) {
  const palette = {
    sky:     "border-sky-500/30 bg-sky-500/10 text-sky-200",
    violet:  "border-violet-500/30 bg-violet-500/10 text-violet-200",
    fuchsia: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-200",
  }[color] || "border-zinc-700 bg-zinc-800/50 text-zinc-300";
  const dt = at ? new Date(at) : null;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 font-medium ${palette}`}
      title={at ? `Trimis: ${dt.toLocaleString("ro-RO")}` : ""}
    >
      {icon}
      {label}
      {dt && <span className="opacity-70">· {dt.toLocaleDateString("ro-RO")}</span>}
    </span>
  );
}


/** Feb 2026 — pill compact pentru status trimitere (MR / R1 / R2) în
 *  lista de conversații. Verde cu bifă = trimis, gri = netrimis.
 *  Tooltip cu data trimiterii când există. */
function SendStatusBadge({ label, sent, sentAt, title }) {
  const dt = sentAt ? new Date(sentAt) : null;
  const cls = sent
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
    : "border-zinc-800 bg-zinc-900/60 text-zinc-500";
  const tip = sent && dt
    ? `${title} — trimis: ${dt.toLocaleString("ro-RO")}`
    : `${title} — netrimis`;
  return (
    <span
      data-testid={`wa-conv-badge-${label.toLowerCase()}${sent ? "-sent" : "-pending"}`}
      title={tip}
      className={`inline-flex items-center gap-0.5 rounded border px-1 py-0.5 font-mono text-[9px] font-semibold uppercase ${cls}`}
    >
      {sent ? <CheckCircle2 className="h-2.5 w-2.5" /> : <Bell className="h-2.5 w-2.5 opacity-50" />}
      {label}
    </span>
  );
}
